
import { onAuthStateChanged } from 'firebase/auth/web-extension';
import { auth } from '../config/firebaseConfig';
import { Sentry } from '../utils/sentry';

const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API_URL;

// Most operations are quick CRUD and should fail fast if the backend hangs.
const DEFAULT_TIMEOUT_MS = 10_000;
// AI generations (Anthropic-backed reflections, insights, note analysis/transform)
// take longer than CRUD, so those callers pass this budget explicitly.
//
// 32s is deliberately sized to the SERVER envelope, not to a guess:
//   Anthropic per-call budget  INSIGHTS_AI_TIMEOUT_MS = 20s (schema.js)
//   server hard request timeout server.timeout        = 30s (server.js)
// The client must sit JUST PAST the server's own 30s cut-off so the server wins
// the race and the user sees the backend's real error instead of a bare abort.
// The old 60s value let a user watch a skeleton for a full minute on a request
// the server had already killed at 30s.
//
// If you raise INSIGHTS_AI_TIMEOUT_MS or server.timeout, raise this too — they
// are one budget expressed in three places.
export const AI_TIMEOUT_MS = 32_000;

/**
 * Thrown when OUR AbortController fired, i.e. the request outlived timeoutMs.
 *
 * It exists so a timeout is a real, renderable error: a bare fetch abort
 * surfaces as a DOMException reading "The user aborted a request." — which is
 * both wrong (the user did nothing) and untranslatable UI copy. Callers that
 * render `err.message` directly (e.g. useQuranInsights) get usable text with no
 * change on their side.
 */
export class RequestTimeoutError extends Error {
  readonly timeoutMs: number;
  constructor(timeoutMs: number) {
    super('This is taking longer than expected. Please try again.');
    this.name = 'RequestTimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

/**
 * A GraphQL-level error (HTTP 200 with `errors`, or a non-2xx carrying them).
 * Keeps the server's `extensions.code` so callers can branch on the contract's
 * codes (PRO_REQUIRED, NOT_FOUND, …) instead of on message text. Still an
 * Error with the server's message, so every existing `err.message` caller is
 * unchanged.
 */
export class GraphqlResponseError extends Error {
  readonly code: string | null;
  readonly extensions: Record<string, unknown>;
  constructor(message: string, extensions: Record<string, unknown> | undefined) {
    super(message);
    this.name = 'GraphqlResponseError';
    this.extensions = extensions ?? {};
    this.code = typeof this.extensions.code === 'string' ? this.extensions.code : null;
  }
}

/** The contract error code of a failed request, or null (network, timeout, untyped). */
export const graphqlErrorCode = (err: unknown): string | null =>
  err instanceof GraphqlResponseError ? err.code : null;

export const isGraphqlConfigured = (): boolean => !!GRAPHQL_API_URL;

/**
 * Returns a fresh Firebase ID token.
 *
 * Firebase's getIdToken() automatically refreshes the token when it is within
 * 5 minutes of expiry, so callers never need to force-refresh manually.
 *
 * If auth.currentUser is null (e.g. the extension just reloaded and Firebase
 * hasn't restored the session from IndexedDB yet), we wait up to 5 seconds for
 * onAuthStateChanged to fire before giving up.
 */
// Dev-only: in preview/screenshot mode there is no Firebase user, so resolve a
// sentinel token. The backend (with DEV_AUTH_BYPASS=true) ignores the token value.
const PREVIEW_WITHOUT_AUTH = process.env.REACT_APP_PREVIEW_WITHOUT_AUTH === 'true';

const getIdToken = (forceRefresh = false): Promise<string> => {
  if (auth.currentUser) {
    return auth.currentUser.getIdToken(forceRefresh);
  }

  if (PREVIEW_WITHOUT_AUTH) {
    return Promise.resolve('demo-preview-token');
  }

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unsubscribe();
      reject(new Error('Session expired. Please sign in again.'));
    }, 5000);

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      clearTimeout(timer);
      unsubscribe();
      if (user) {
        resolve(user.getIdToken());
      } else {
        reject(new Error('Session expired. Please sign in again.'));
      }
    });
  });
};

/**
 * True when the response carries the backend's email-verification gate error.
 * Matched on `extensions.code`, which is why the resolver throws a real
 * GraphQLError — a plain Error would be masked to "Unexpected error." in prod
 * and this check could never fire.
 */
const EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED';

export const hasEmailNotVerifiedError = (json: any): boolean =>
  Array.isArray(json?.errors) &&
  json.errors.some((e: any) => e?.extensions?.code === EMAIL_NOT_VERIFIED);

const doRequest = async (token: string, query: string, variables: Record<string, unknown> | undefined, timeoutMs: number): Promise<Response> => {
  const controller = new AbortController();
  // `timedOut` distinguishes OUR abort from any other AbortError so we only
  // rewrite the error we actually caused.
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    return await fetch(GRAPHQL_API_URL!, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ query, variables }),
      signal: controller.signal,
    });
  } catch (err) {
    if (timedOut) throw new RequestTimeoutError(timeoutMs);
    throw err;
  } finally {
    clearTimeout(timer);
  }
};

export const graphqlRequest = async <T>(
  query: string,
  variables?: Record<string, unknown>,
  opts?: { timeoutMs?: number },
): Promise<T> => {
  if (!GRAPHQL_API_URL) throw new Error('REACT_APP_GRAPHQL_API_URL is not configured');

  const operationName = query.match(/\b(?:query|mutation)\s+(\w+)/)?.[1] || 'anonymous';
  const timeoutMs = opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  let token = await getIdToken();
  let res: Response;
  try {
    res = await doRequest(token, query, variables, timeoutMs);

    // On 401, force-refresh the Firebase token and retry once (expected path — not reported)
    if (res.status === 401) {
      token = await getIdToken(true);
      res = await doRequest(token, query, variables, timeoutMs);
    }
  } catch (err) {
    // Network failure or a client-side timeout — invisible until now. Timeouts
    // are tagged separately so an ops read can tell "backend unreachable" from
    // "backend too slow"; a spike in kind:'timeout' on an AI operation means the
    // server-side budget, not the network, is the thing to look at.
    const kind = err instanceof RequestTimeoutError ? 'timeout' : 'network';
    Sentry.captureException(err, { extra: { operationName, kind, timeoutMs } });
    throw err;
  }

  let json = await res.json();

  // Email-verification gate: the backend returns HTTP 200 with a GraphQL error,
  // so the 401 retry above never fires for it.
  //
  // `email_verified` is a claim baked into the ID token, and getIdToken() only
  // auto-refreshes near expiry — so right after a user verifies, the cached token
  // still says false and every request is rejected for up to ~55 minutes. Force a
  // refresh and retry once. (The client also refreshes at the moment of
  // verification; this is the backstop for tokens minted before that, e.g. a tab
  // restored from a previous session.)
  if (hasEmailNotVerifiedError(json)) {
    try {
      token = await getIdToken(true);
      res = await doRequest(token, query, variables, timeoutMs);
      json = await res.json();
    } catch {
      // Fall through and surface the original gate error below.
    }
  }

  if (!res.ok) {
    const error = new GraphqlResponseError(
      json?.errors?.[0]?.message || `GraphQL request failed (HTTP ${res.status})`,
      json?.errors?.[0]?.extensions,
    );
    Sentry.captureException(error, { extra: { operationName, status: res.status } });
    throw error;
  }
  // Resolver-level errors carry user-facing messages (paywall, validation) —
  // the backend logs/reports the unexpected ones, so don't double-report here.
  if (json?.errors?.length) {
    throw new GraphqlResponseError(json.errors[0].message || 'GraphQL request failed', json.errors[0].extensions);
  }
  return json.data as T;
};
